import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, X, Smartphone, RefreshCw, Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);
  
  // Use ref to store current timestamp for localStorage operations
  const timestampRef = useRef(Date.now());

  // Masa pop-up boleh laras oleh superadmin (saat)
  const timingRef = useRef({ install: 3, notif: 10 });
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/popup-timing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) timingRef.current = { install: d.install_delay_seconds, notif: d.notif_delay_seconds };
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // Check if dismissed recently (within 7 days for install, 1 day for notification)
    const installDismissedTime = localStorage.getItem('pwa-install-dismissed');
    const notifDismissedTime = localStorage.getItem('pwa-notif-dismissed');
    const currentTime = timestampRef.current;
    
    const showInstall = !installDismissedTime || 
      (currentTime - parseInt(installDismissedTime)) / (1000 * 60 * 60 * 24) >= 7;
    
    const showNotif = !notifDismissedTime || 
      (currentTime - parseInt(notifDismissedTime)) / (1000 * 60 * 60 * 24) >= 1;

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (showInstall) {
        setTimeout(() => setShowInstallPrompt(true), timingRef.current.install * 1000);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      toast.success('Taska Ummu Qaseh berjaya dipasang!');
      
      // Show notification prompt after install
      if (showNotif && notificationPermission === 'default') {
        setTimeout(() => setShowNotificationPrompt(true), timingRef.current.notif * 1000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Service worker registration is handled by index.js
    // We only store the registration for manual update check button

    // Show notification prompt for installed app after some usage
    if (isInstalled && showNotif && notificationPermission === 'default') {
      setTimeout(() => setShowNotificationPrompt(true), timingRef.current.notif * 1000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, notificationPermission]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted install');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleInstallDismiss = useCallback(() => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }, []);

  const handleRequestNotification = useCallback(async () => {
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        toast.success('Notifikasi diaktifkan!');
        
        // Subscribe to push notifications if supported
        if (swRegistration && 'pushManager' in swRegistration) {
          try {
            // For demo - in production, use your VAPID public key
            const subscription = await swRegistration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(
                'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
              )
            });
            console.log('Push subscription:', subscription);
            
            // Send subscription to server (if you have endpoint)
            // await fetch('/api/push-subscription', {
            //   method: 'POST',
            //   headers: { 'Content-Type': 'application/json' },
            //   body: JSON.stringify(subscription)
            // });
          } catch (error) {
            console.log('Push subscription failed:', error);
          }
        }
      } else {
        toast.info('Notifikasi tidak diaktifkan');
      }
    } catch (error) {
      console.error('Notification permission error:', error);
    }
    
    setShowNotificationPrompt(false);
    localStorage.setItem('pwa-notif-dismissed', Date.now().toString());
  }, [swRegistration]);

  const handleNotificationDismiss = useCallback(() => {
    setShowNotificationPrompt(false);
    localStorage.setItem('pwa-notif-dismissed', Date.now().toString());
  }, []);

  // Helper function for VAPID key conversion
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  return (
    <>
      {/* Install Prompt - positioned above version update */}
      {showInstallPrompt && !isInstalled && (
        <div className="fixed bottom-28 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl shadow-2xl p-4 text-white">
            <button
              onClick={handleInstallDismiss}
              className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
              data-testid="install-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm">Install Taska Ummu Qaseh</h3>
                <p className="text-xs text-white/80 mt-0.5">
                  Akses lebih pantas dari skrin utama telefon anda
                </p>
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <Button
                onClick={handleInstallDismiss}
                variant="ghost"
                size="sm"
                className="flex-1 text-white/80 hover:text-white hover:bg-white/10"
                data-testid="install-later-btn"
              >
                Nanti
              </Button>
              <Button
                onClick={handleInstall}
                size="sm"
                className="flex-1 bg-white text-pink-600 hover:bg-white/90"
                data-testid="install-now-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Install
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Update Prompt - Removed, handled by index.js */}

      {/* Notification Permission Prompt - positioned above install prompt */}
      {showNotificationPrompt && notificationPermission === 'default' && (
        <div className="fixed bottom-52 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-2xl p-4 text-white">
            <button
              onClick={handleNotificationDismiss}
              className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
              data-testid="notif-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm">Aktifkan Notifikasi</h3>
                <p className="text-xs text-white/80 mt-0.5">
                  Dapatkan pemberitahuan tentang aktiviti anak anda
                </p>
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <Button
                onClick={handleNotificationDismiss}
                variant="ghost"
                size="sm"
                className="flex-1 text-white/80 hover:text-white hover:bg-white/10"
                data-testid="notif-later-btn"
              >
                <BellOff className="w-4 h-4 mr-1" />
                Nanti
              </Button>
              <Button
                onClick={handleRequestNotification}
                size="sm"
                className="flex-1 bg-white text-amber-600 hover:bg-white/90"
                data-testid="notif-enable-btn"
              >
                <Bell className="w-4 h-4 mr-1" />
                Aktifkan
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
